// Filename: SearchClient.js
// Description: This component renders the components in /searchResults.
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import axios from "axios";
import SearchForm from "./searchClientResults/SearchForm";
import SearchInstructions from "./searchClientResults/SearchInstructions";
import ClientList from "./searchClientResults/ClientList";
import ClientView from "./searchClientResults/ClientView";
import HouseholdAidHistory from "./searchClientResults/HouseholdAidHistory";
import introJs from 'intro.js';

function SearchClient({ contentOpen, setContentOpen, addCheckedOutProfileClientID, userPermissions }) {
  const [instructionsIsOpen, setInstructionsIsOpen] = React.useState(true);
  const [clientInfoIsOpen, setClientInfoIsOpen] = React.useState(false);
  const [clients, setClients] = React.useState([]);
  const [selectedClient, setSelectedClient] = React.useState(null);
  const [aidHistoryRefreshKey, setAidHistoryRefreshKey] = React.useState(0);

  const triggerHouseholdAidRefresh = () => setAidHistoryRefreshKey(prev => prev + 1);
  const navigate = useNavigate();

  let left;
  let right;

  async function handleSearchClient(searchParams) {
    const url = process.env.REACT_APP_BACKURL + "/api/foodPantry/client";
    setInstructionsIsOpen(false);

    try {
      const sessionID = Cookies.get("sessionID");
      if (!sessionID) {
        alert("Session expired. Please log in again.");
        navigate("/SignIn");
        return;
      }
      const response = await axios.get(url, {
        params: searchParams,
        headers: {
          Authorization: `Bearer ${sessionID}`,
        },
        withCredentials: true,
      });
      setClients(response.data.clients || []);
      setSelectedClient(null);
    } catch (error) {
      console.error("Error searching for clients:", error);
      alert("Search failed. Please try again.");
    }
  }

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setClientInfoIsOpen(true);
    setAidHistoryRefreshKey(0);
  };

  const handleViewClientProfile = () => {
    addCheckedOutProfileClientID(selectedClient.clientID);
    setContentOpen("profile");
  }

  if (!clientInfoIsOpen) {
    left = <SearchForm onSearch={handleSearchClient} />;
    if (clients.length > 0) {
      right = <ClientList clients={clients} onSelect={handleSelectClient} />;
    } else if (instructionsIsOpen) {
      right = <SearchInstructions />;
    } else {
      right = (
        <div className="card border border-light m-0 h-100 p-5 text-center">
          <h3>No Clients Found</h3>
          <p>Try broadening your search criteria.</p>
        </div>
      );
    }
  } else {
    left = (
      <ClientView
        onBack={() => setClientInfoIsOpen(false)}
        client={selectedClient}
        onVisitLogged={triggerHouseholdAidRefresh}
        onHouseholdUpdate={triggerHouseholdAidRefresh}
      />
    );
    right = (
      <HouseholdAidHistory
        client={selectedClient}
        refreshKey={aidHistoryRefreshKey}
      />
    );
  }


  // Tour state
  const [tourStage, setTourStage] = useState("none"); // "none", "main", "secondary"
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [manualTour, setManualTour] = useState(false);

  // Tour steps
  const mainTourSteps = [
    {
      element: ".client-search-title",
      intro: "This is the client search page where you can find and manage clients.",
      position: "bottom"
    },
    {
      element: "#Left\\ Column",
      intro: "Search Clients using any of the input fields.",
      position: "right"
    },
    {
      element: "#Right\\ Column",
      intro: "The results of your search will appear here after selecting someone you can do things like Log a Visit, Edit a Client, Edit Their Household or checkout their profile page.",
      position: "left"
    }
  ];

  const secondaryTourSteps = [
    {
      element: "#Left\\ Column",
      intro: "After searching, this left column shows the selected client's details.",
      position: "right"
    },
    {
      element: "#Right\\ Column",
      intro: "And this right column shows the client's household aid history.",
      position: "left"
    }
  ];

  // Helper to check if a tour is running
  function isIntroJsActive() {
    const introInstance = window.introJs && window.introJs();
    const overlay = document.querySelector('.introjs-overlay');
    return (introInstance && introInstance._currentStep !== undefined) || (overlay && overlay.style.display !== 'none');
  }

  // Main tour effect
  useEffect(() => {
    if (tourStage === "main" && !isTourRunning && !isIntroJsActive()) {
      setIsTourRunning(true);
      setTimeout(() => {
        const intro = introJs();
        intro.setOptions({
          steps: mainTourSteps,
          showBullets: false
        });
        intro.start();
        intro.oncomplete(() => {
          setIsTourRunning(false);
          setTourStage("none");
          handleTourRedirect();
        });
        intro.onexit(() => {
          setIsTourRunning(false);
          setTourStage("none");
          if (manualTour) {
            setManualTour(false);
          }
        });
      }, 300); // 300ms delay before starting tour
    }
  }, [tourStage, isTourRunning]);

  // // Secondary tour effect
  // useEffect(() => {
  //   if (tourStage === "secondary" && !isTourRunning && !isIntroJsActive()) {
  //     setIsTourRunning(true);
  //     const intro = introJs();
  //     intro.setOptions({
  //       steps: secondaryTourSteps,
  //       showBullets: false
  //     });
  //     intro.start();
  //     intro.oncomplete(() => {
  //       setIsTourRunning(false);
  //       handleTourRedirect();
  //     });
  //     intro.onexit(() => {
  //       setIsTourRunning(false);
  //       handleTourRedirect();
  //     });
  //   }
  // }, [tourStage, isTourRunning]);

  // Redirection logic after tour
  function handleTourRedirect() {
    if (manualTour) {
      setManualTour(false);
      return;
    }
    else {
      if (userPermissions.includes("registerClient")) {
        setContentOpen("registerClient");
      } else if (userPermissions.includes("foodPantry")) {
        setContentOpen("foodPantry");
      } else if (userPermissions.includes("reports")) {
        setContentOpen("reports");
      } else {
        setContentOpen("searchOrganizations");
      }
    }
  }

  const [tourRanOnce, setTourRanOnce] = useState(false);

  // Automatic tour trigger (continuously re-prompt tour start logic)
  useEffect(() => {
    if (!userPermissions) return;
    const fullTourNeeded = JSON.parse(localStorage.getItem("FullTourNeeded"));
    if (!fullTourNeeded) return;

    if (contentOpen !== "searchClient") return;
    if (tourRanOnce) return;
    // Continuously poll for required elements and tour state
    let pollInterval = setInterval(() => {
      const titleExists = document.querySelector('.client-search-title');
      const leftExists = document.querySelector('#Left\\ Column');
      const rightExists = document.querySelector('#Right\\ Column');
      const tourAlreadyRunning = isTourRunning || isIntroJsActive();
      if (titleExists && leftExists && rightExists && !tourAlreadyRunning) {
        setTourStage("main");
        setTourRanOnce(true);
      }
    }, 150);

    // Cleanup
    return () => clearInterval(pollInterval);
  }, [userPermissions, isTourRunning]);

  // Manual tour trigger
  function handleManualTour() {
    if (isTourRunning || isIntroJsActive()) return;
    setTourStage("main");
    setManualTour(true);
  }

  return (
    <React.Fragment>
      <div className="card col-12 col-md-10 offset-0 offset-md-1 mt-5 card-body container my-3">
        <div className="card-header d-flex flex-wrap align-items-center">
          <h3 className="mb-0 d-flex align-items-center client-search-title"

            data-step="1">
            <i className="bi bi-search pe-3"></i>
            Client Search
            <i
              className="bi bi-question-circle d-none d-sm-inline"
              style={{
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#4d59df',
                marginRight: '0.75rem',
                marginLeft: '0.25rem'
              }}
              onClick={handleManualTour}
            />
          </h3>
          <div className="flex-grow-1"></div>
          <div className="text-end">
            {clientInfoIsOpen && (
              <button
                className="btn btn-primary mt-2"
                onClick={handleViewClientProfile}
              >
                <i className="bi bi-person-lines-fill me-2"></i>
                View Client Profile Page
              </button>
            )}
          </div>
        </div>
        <div className="card-body">
          <div className="row gx-3">
            <div className="col-lg-6 col-md-12 col-12" id="Left Column">
              {left}
            </div>
            <div className="col-lg-6 col-md-12 col-12" id="Right Column">
              {right}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default SearchClient;